import { Popover } from '@material-ui/core'
import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { BaseThemeType, Button, MenuItem, Select, SortIcon, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useRef, useState } from 'react'
import { FINANCE_PRODUCT_CHOICES } from 'resources/financeProducts/constants'
import { CheckoutFinanceProductType, CheckoutFinanceSorting, CheckoutFinancingPricingParams } from '../types'

// We're hardcoding the product choices to only include loan for now because we don't have options for other products yet
const PRODUCT_CHOICES = FINANCE_PRODUCT_CHOICES.filter((choice) => choice.id === 'loan')

type PropTypes = {
  updatePricingParams: (newParams: CheckoutFinancingPricingParams) => void
  pricingParams: CheckoutFinancingPricingParams
}

const Row = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
})

const ButtonsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginLeft: '16px',
  gap: '12px',
})

const PopoverMenuContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '8px',
})

const PopoverMenuItem = styled('div')<{ isSelected: boolean }>(
  ({ theme, isSelected }: { theme: BaseThemeType; isSelected: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
      opacity: 0.5,
    },
    fontWeight: isSelected ? 600 : 400,
    backgroundColor: isSelected ? theme.palette.grey[100] : 'transparent',
    borderRadius: '4px',
  })
)

const FinanceMarketplaceSorting = (props: PropTypes) => {
  const [sorting, setSorting] = useState<CheckoutFinanceSorting>(
    props.pricingParams?.filters?.sorting || 'lowest_monthly_payment'
  )
  const [sortingOptionsOpen, setSortingOptionsOpen] = useState<boolean>(false)
  const [selectedProduct, setSelectedProduct] = useState<CheckoutFinanceProductType | '' | null>(
    props.pricingParams?.filters?.product_type || ''
  )
  const translate = useTranslate()
  const popoverRef = useRef<HTMLButtonElement>(null)

  const onSortingChange = (newValue: CheckoutFinanceSorting) => {
    if (newValue !== sorting) {
      logAmplitudeEventAllowCustomers('marketplace_sorting_changed', {
        sorting: newValue,
        org_id: props.pricingParams.org_id,
        project_id: props.pricingParams.project_id,
      })
      setSorting(newValue)
      props.updatePricingParams({
        ...props.pricingParams,
        filters: { ...props.pricingParams.filters, sorting: newValue },
      })
    }
    setSortingOptionsOpen(false)
  }

  const onProductChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value || null
    const productType = newValue as CheckoutFinanceProductType | null

    if (newValue !== selectedProduct) {
      setSelectedProduct(productType)
      props.updatePricingParams({
        ...props.pricingParams,
        filters: {
          ...props.pricingParams.filters,
          product_type: productType,
        },
      })

      if (productType) {
        logAmplitudeEventAllowCustomers('marketplace_filter_changed', {
          filter_type: 'product_type',
          value: productType,
        })
      }
    }
  }

  return (
    <Row>
      <Typography textVariant="h5">{translate('Available Finance Options')}</Typography>
      <ButtonsContainer>
        <Button variant="contained" onClick={() => setSortingOptionsOpen(true)} ref={popoverRef}>
          <SortIcon />
          {translate('Sort')}
        </Button>
        <Popover
          id={'finance-marketplace-sorting'}
          open={sortingOptionsOpen}
          anchorEl={popoverRef?.current}
          onClose={() => setSortingOptionsOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <PopoverMenuContainer>
            <PopoverMenuItem
              onClick={() => onSortingChange('lowest_monthly_payment')}
              isSelected={sorting === 'lowest_monthly_payment'}
            >
              Lowest Monthly Payment
            </PopoverMenuItem>
            <PopoverMenuItem
              onClick={() => onSortingChange('lowest_total_repayable')}
              isSelected={sorting === 'lowest_total_repayable'}
            >
              Lowest Total Repayable
            </PopoverMenuItem>
            <PopoverMenuItem onClick={() => onSortingChange('shortest_term')} isSelected={sorting === 'shortest_term'}>
              Shortest Term
            </PopoverMenuItem>
          </PopoverMenuContainer>
        </Popover>
        {PRODUCT_CHOICES.length > 1 && (
          <Select
            value={selectedProduct}
            onChange={onProductChange}
            displayEmpty
            renderValue={(value) => {
              if (!value || value === '') {
                return translate('All Products')
              }

              const selectedChoice = PRODUCT_CHOICES.find((choice) => choice.id === value)
              return selectedChoice?.label || String(value)
            }}
          >
            <MenuItem value="">{translate('All Products')}</MenuItem>
            {PRODUCT_CHOICES.map((choice) => (
              <MenuItem key={choice.id} value={choice.id}>
                {choice.label}
              </MenuItem>
            ))}
          </Select>
        )}
      </ButtonsContainer>
    </Row>
  )
}

export default FinanceMarketplaceSorting
